import {
  closeLeadFormModal,
  openLeadFormModal,
  setLocalStorageData,
  toggleErrorState,
  toggleLoading,
  updateLeadFormState,
  updateShowGenericForm,
  updateSubmittingFormFirstTime,
} from "@Components/LeadFormModal/data/leadFormReducer";
import { onSubmitLeadFormOne } from "@Components/LeadFormModal/utils";
import getCookie from "helpers/getCookie";
import { gtagDuplicateEventEmitter } from "helpers/gtagEventEmitter";
import openChatWidget from "helpers/openChatWidget";
import { FLOATING_BUTTON_ENTRY_POINTS, GROWTH_BOOK_FEATURE_FLAG_NAMES } from "constants/index";
import updateInventoryData from "./updateInventoryData";
import { openBookForm } from "@Components/LeadForm/utils";
import { OPENING_SOURCE } from "../constants";
import { isHappyFlowForUSA } from ".";
import { logAppEvent } from "@Utils/appAnalytics";
import { getRoomDetailsEventProps } from "helpers/roomDetailsTracking";
import { getRecordedPropertyNameForPostHog, identifyPostHogUserByEmail } from "@Utils/posthog";
import { APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import getUserIpAddress from "@Utils/getUserIpAddress";

const handleLeadAPICall = async ({
  dispatch,
  region,
  inventory,
  isLeadFormFilled,
  inModal,
  openingSource,
  formData,
  filters,
  history,
  propertyId = null,
  isPaymentBeforeBookform,
  isZendeskFlowAfterLead = false,
  isDesktop = false,
  isMobile = false,
  redirectWithoutSuccessScreen = false,
  isWhitelabel = false,
  isDemoSubdomain = false,
  partner_uuid = "",
  isBookFormEnabledForInactiveProperty = false,
  roomId,
  tenancyId,
}: any) => {
  dispatch(toggleLoading({ isLoading: true }));
  const isOptIn = getCookie("isSmsOptIn") === "true";

  const userIpAddress = getUserIpAddress();

  const response = await onSubmitLeadFormOne({
    data: formData,
    inventory,
    isLeadFormFilled,
    inModal,
    redirectCB: () => dispatch(closeLeadFormModal()),
    inventoryRegion: region,
    openingSource,
    isHappyFlow: true,
    filters,
    isOptIn,
    history,
    isPaymentBeforeBookform,
    isWhitelabel,
    isDemoSubdomain,
    partner_uuid,
    isBookFormEnabledForInactiveProperty,
    ...(userIpAddress && { ip_address: userIpAddress }),
  });

  if (response?.message === "success") {
    const queryString = response?.redirectionURL.split("?")[1];
    const urlParams = new URLSearchParams(queryString);
    const leadId = urlParams.get("lead_id");

    await identifyPostHogUserByEmail(formData?.email, {
      funnel: "lead",
      lead_id: leadId,
      opening_source: openingSource,
      property_id: propertyId,
      user_phone: formData?.mobileNumber,
      country_code: formData?.code,
      // Slug, name and timestamp move together or not at all — a lone
      // timestamp would refresh a stale `lead_property` from an earlier lead.
      ...(inventory?.canonical_name
        ? {
            lead_property: inventory.canonical_name,
            // The inventory often has no name; reuse the one recorded for this slug.
            ...(() => {
              const displayName =
                inventory?.name || getRecordedPropertyNameForPostHog(inventory.canonical_name);
              return displayName ? { lead_property_name: displayName } : {};
            })(),
            lead_property_at: new Date().toISOString(),
          }
        : {}),
      ...(response?.data?.user_id ? { user_id: String(response.data.user_id) } : {}),
    });

    logAppEvent({
      eventName: APP_EVENTS.LEAD_CREATED,
      payload: {
        lead_created_from: openingSource,
        lead_id: leadId,
        user_name: formData?.fullName,
        user_email: formData?.email,
        country_code: formData?.code,
        user_phone: formData?.mobileNumber,
        property_id: propertyId,
        ...(inventory?.canonical_name && { canonical_name: inventory.canonical_name }),
        ...(inventory?.name && { property_name: inventory.name }),
        ...(inventory?.pricing?.currency && { currency: inventory.pricing.currency }),
        ...(region?.id && { region_id: region.id }),
        ...(region?.name && { region_name: region.name }),
        ...(region?.canonical_name && { region_canonical_name: region.canonical_name }),
        ...(roomId && { room_id: roomId }),
        ...(tenancyId && { tenancy_id: tenancyId }),
        ...(openingSource === "apply_for_fall" && { is_apply_for_fall_lead: true }),
        ...(openingSource === "apply_for_fall" &&
          inventory?.future_intake_name && { future_intake_name: inventory.future_intake_name }),
        ...getRoomDetailsEventProps(inventory?.room_details_section),
      },
    });

    dispatch(setLocalStorageData());
    updateInventoryData({
      openingSource,
      regionCanonicalName: region?.canonical_name,
      dispatch,
      inventory,
      propertyId,
    });
    if (inModal) {
      dispatch(updateShowGenericForm({ showGenericForm: false }));
    }

    if (
      isZendeskFlowAfterLead &&
      (openingSource === "clicked_book_enquire_now_property" ||
        openingSource === "search_inventory_enquire" ||
        openingSource === "questions_enquiry_connect_cta")
    ) {
      dispatch(closeLeadFormModal());
      // Check feature flag using GrowthBook instance
      const growthbook = typeof window !== "undefined" && (window as any).__GROWTHBOOK__;
      const isAmberCopilotEnabled =
        growthbook?.isOn(GROWTH_BOOK_FEATURE_FLAG_NAMES.AMBER_COPILOT) || true;

      openChatWidget({
        dispatch,
        isMobile,
        isDesktop,
        isAmberCopilotEnabled,
        entryPoint: FLOATING_BUTTON_ENTRY_POINTS.PAGE_BUTTON,
      });

      gtagDuplicateEventEmitter({
        action: "lead_form_v2_success_page_viewed",
        category: "lead",
        source: openingSource,
        repeat_lead: false,
      });

      updateSubmittingFormFirstTime({ submittingFormFirstTime: false });
    } else {
      dispatch(updateLeadFormState({ submittingFormFirstTime: false, leadFormPage: 2 }));
      if (openingSource !== "book_now") {
        if (
          inventory?.available &&
          (openingSource === OPENING_SOURCE.CLICKED_BOOK_ENQUIRE_NOW_PROPERTY ||
            openingSource === OPENING_SOURCE.SEARCH_INVENTORY_ENQUIRE) &&
          redirectWithoutSuccessScreen
        ) {
          if (isHappyFlowForUSA(inventory, openingSource)) {
            dispatch(
              openLeadFormModal({ inventory, inModal: true, leadFormPage: 2, openingSource }),
            );
          } else {
            openBookForm({ url: response.redirectionURL, history });
          }
        } else {
          dispatch(openLeadFormModal({ inventory, inModal: true, leadFormPage: 2, openingSource }));
        }
      }
    }
  } else if (response?.message === "failure") {
    // write error logic
    dispatch(toggleErrorState({ showError: true }));
  }
  dispatch(toggleLoading({ isLoading: false }));
};

export default handleLeadAPICall;
